import {
  ProtocolPatientStatusDetail,
  ProtocolPatientStatusEnum,
  ProtocolPatientStatusStatistics
} from '../../../models/Entities/Protocols/ProtocolPatientStatusStatistics';
import { ThemeMode } from '../../../theme/guideTokens';

//#region Etapas

/**
 * Etapa del estudio en la que está un paciente, según su estado.
 *
 * - Selección: todavía se está evaluando si entra (Screening, PreScreening).
 * - Randomizado: ya entró al estudio, sea cual sea su situación después
 *   (tratamiento, seguimiento, discontinuado, perdido, finalizado...).
 * - Fallas de selección: sólo ScreenFailure, un paciente que se evaluó y no
 *   entró. No está en selección —la selección terminó— ni fue randomizado.
 * - Sin etapa: un código que el glosario no conoce. No va con las fallas de
 *   selección, que son únicamente ScreenFailure, porque no hay base para
 *   ubicarlo en ninguna etapa.
 */
export type PatientStage = 'selection' | 'randomized' | 'screenFailure' | 'unknown';

/**
 * Orden de lectura: primero la selección, después quienes no la pasaron —que
 * salen de ella— y recién ahí los randomizados.
 */
export const STAGE_ORDER: PatientStage[] = ['selection', 'screenFailure', 'randomized', 'unknown'];

export const STAGE_LABEL: Record<PatientStage, string> = {
  selection: 'Etapa de selección',
  randomized: 'Randomizados',
  screenFailure: 'Fallas de selección',
  unknown: 'Sin etapa'
};

export const statusStage = (status: number): PatientStage => {
  switch (status) {
    case ProtocolPatientStatusEnum.Screening:
    case ProtocolPatientStatusEnum.PreScreening:
      return 'selection';
    case ProtocolPatientStatusEnum.Random:
    case ProtocolPatientStatusEnum.Treatment:
    case ProtocolPatientStatusEnum.FollowUp:
    case ProtocolPatientStatusEnum.LostToFollowUp:
    case ProtocolPatientStatusEnum.WithoutDrugs:
    case ProtocolPatientStatusEnum.Discontinuation:
    case ProtocolPatientStatusEnum.EndParticipationInProtocol:
      return 'randomized';
    case ProtocolPatientStatusEnum.ScreenFailure:
      return 'screenFailure';
    default:
      return 'unknown';
  }
};

//#endregion Etapas

export interface PatientStatusRow {
  status: number;
  label: string;
  count: number;
  /** Porcentaje sobre el total del protocolo, con un decimal. */
  percentage: number;
  color: string;
  stage: PatientStage;
}

export interface PatientStageGroup {
  stage: PatientStage;
  label: string;
  count: number;
  /** Porcentaje de la etapa sobre el total del protocolo, con un decimal. */
  percentage: number;
  rows: PatientStatusRow[];
}

/**
 * Un color fijo por estado, el mismo en cualquier protocolo.
 *
 * El rojo es exclusivo de ScreenFailure: es el único estado que dice "no entró", y
 * si otro estado compartiera el rojo —o un naranja que se le parezca— esa señal se
 * perdería. Los estados de selección van en ámbares, como su etapa; los
 * randomizados en azules, verdes, violetas y grises.
 */
export const STATUS_COLOR: Record<number, string> = {
  [ProtocolPatientStatusEnum.Screening]: '#c19c00',
  [ProtocolPatientStatusEnum.PreScreening]: '#8f6b00',
  [ProtocolPatientStatusEnum.ScreenFailure]: '#d13438',
  [ProtocolPatientStatusEnum.Random]: '#0078d4',
  [ProtocolPatientStatusEnum.Treatment]: '#00b294',
  [ProtocolPatientStatusEnum.FollowUp]: '#8764b8',
  [ProtocolPatientStatusEnum.LostToFollowUp]: '#8a8886',
  [ProtocolPatientStatusEnum.WithoutDrugs]: '#005b70',
  [ProtocolPatientStatusEnum.Discontinuation]: '#5c2e91',
  [ProtocolPatientStatusEnum.EndParticipationInProtocol]: '#107c10'
};

/**
 * Los mismos tonos aclarados para el tema oscuro y saturados para alto contraste, así cada
 * estado se distingue sobre la superficie oscura / negra de la guía. La paleta de gráficos
 * de la guía tiene 8 colores y acá hacen falta 10 categorías fijas: por eso viven acá.
 */
const STATUS_COLOR_DARK: Record<number, string> = {
  [ProtocolPatientStatusEnum.Screening]: '#e6c74c',
  [ProtocolPatientStatusEnum.PreScreening]: '#c9a227',
  [ProtocolPatientStatusEnum.ScreenFailure]: '#ff8a80',
  [ProtocolPatientStatusEnum.Random]: '#64b5f6',
  [ProtocolPatientStatusEnum.Treatment]: '#4dd0bc',
  [ProtocolPatientStatusEnum.FollowUp]: '#b39ddb',
  [ProtocolPatientStatusEnum.LostToFollowUp]: '#a0a8b3',
  [ProtocolPatientStatusEnum.WithoutDrugs]: '#4fb3c8',
  [ProtocolPatientStatusEnum.Discontinuation]: '#ce93d8',
  [ProtocolPatientStatusEnum.EndParticipationInProtocol]: '#81c995'
};

const STATUS_COLOR_HIGH_CONTRAST: Record<number, string> = {
  [ProtocolPatientStatusEnum.Screening]: '#ffe600',
  [ProtocolPatientStatusEnum.PreScreening]: '#ffb13b',
  [ProtocolPatientStatusEnum.ScreenFailure]: '#ff7a7a',
  [ProtocolPatientStatusEnum.Random]: '#80d8ff',
  [ProtocolPatientStatusEnum.Treatment]: '#64ffda',
  [ProtocolPatientStatusEnum.FollowUp]: '#e0b8ff',
  [ProtocolPatientStatusEnum.LostToFollowUp]: '#e6e6e6',
  [ProtocolPatientStatusEnum.WithoutDrugs]: '#40c4ff',
  [ProtocolPatientStatusEnum.Discontinuation]: '#ff80ff',
  [ProtocolPatientStatusEnum.EndParticipationInProtocol]: '#5cff8d'
};

const STATUS_COLOR_BY_MODE: Record<ThemeMode, Record<number, string>> = {
  light: STATUS_COLOR,
  dark: STATUS_COLOR_DARK,
  'high-contrast': STATUS_COLOR_HIGH_CONTRAST
};

/** Color de un código que el glosario no conoce. */
export const UNKNOWN_STATUS_COLOR = '#605e5c';

const UNKNOWN_STATUS_COLOR_BY_MODE: Record<ThemeMode, string> = {
  light: UNKNOWN_STATUS_COLOR,
  dark: '#a0a8b3',
  'high-contrast': '#ffffff'
};

/** Color del estado para el tema activo (claro por defecto). */
export const statusColor = (status: number, mode: ThemeMode = 'light'): string =>
  (STATUS_COLOR_BY_MODE[mode] ?? STATUS_COLOR)[status] ?? UNKNOWN_STATUS_COLOR_BY_MODE[mode] ?? UNKNOWN_STATUS_COLOR;

/** Nombre del estado en el glosario del backend, o undefined si el código no existe. */
export const statusEnumName = (status: number): string | undefined =>
  ProtocolPatientStatusEnum[status] as string | undefined;

/**
 * Nombre legible de un estado. Primero el que manda la API en `statusDescription`,
 * que es el que se muestra en el resto del sistema; si falta, el nombre del
 * glosario; y el número sólo para un código que el glosario no conoce.
 */
export const statusLabel = (status: number, description?: string | null): string =>
  description?.trim() || statusEnumName(status) || `Estado ${status}`;

const toPercentage = (count: number, base: number): number =>
  base > 0 ? Math.round((count / base) * 1000) / 10 : 0;

const byStageThenStatus = (a: { stage: PatientStage; status: number }, b: { stage: PatientStage; status: number }) =>
  STAGE_ORDER.indexOf(a.stage) - STAGE_ORDER.indexOf(b.stage) || a.status - b.status;

/**
 * Filas para las celdas, el gráfico y la tabla: agrupadas por etapa y, dentro de
 * cada una, en orden de id de estado.
 *
 * El porcentaje se calcula contra `total` y no contra la suma de los estados:
 * `total` es la cifra que la API afirma para el protocolo, y si alguna vez no
 * coincide con la suma, la diferencia tiene que verse en vez de quedar tapada
 * por un 100% prolijo. Sólo cuando falta el total se usa la suma.
 */
export const buildStatusRows = (
  statistics: ProtocolPatientStatusStatistics | undefined
): PatientStatusRow[] => {
  const statuses = statistics?.statuses ?? [];
  const sum = statuses.reduce((acc, item) => acc + (item.count || 0), 0);
  const base = statistics?.total && statistics.total > 0 ? statistics.total : sum;

  return statuses
    .filter((item) => item.count > 0)
    .map((item) => ({
      status: item.status,
      label: statusLabel(item.status, item.statusDescription),
      count: item.count,
      percentage: toPercentage(item.count, base),
      color: statusColor(item.status),
      stage: statusStage(item.status)
    }))
    .sort(byStageThenStatus);
};

/**
 * Las etapas con sus estados y su subtotal. Selección, randomizados y fallas de
 * selección van siempre, aunque estén vacías: "0 fallas de selección" también es
 * un dato. "Sin etapa" sólo aparece si llega un código que el glosario no conoce.
 */
export const buildStageGroups = (rows: PatientStatusRow[], total: number): PatientStageGroup[] =>
  STAGE_ORDER.map((stage) => {
    const stageRows = rows.filter((row) => row.stage === stage);
    const count = stageRows.reduce((acc, row) => acc + row.count, 0);
    return { stage, label: STAGE_LABEL[stage], count, percentage: toPercentage(count, total), rows: stageRows };
  }).filter((group) => group.stage !== 'unknown' || group.count > 0);

/** Pacientes de un estado, o todos si no se eligió ninguno. */
export const filterDetailByStatus = (
  details: ProtocolPatientStatusDetail[],
  status: number | undefined
): ProtocolPatientStatusDetail[] =>
  status === undefined ? details : details.filter((item) => item.status === status);

/**
 * Fecha de randomización para mostrar. Un paciente todavía no randomizado no la
 * tiene, y eso es un dato —está en screening, por ejemplo—, así que se muestra un
 * guion y no una celda vacía que parezca un error de carga.
 */
export const formatRandomizationDate = (value?: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

/** Minúsculas y sin tildes, para que "Muñoz" se encuentre escribiendo "munoz". */
const normalize = (value?: string | null): string =>
  (value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

/** Pacientes cuyo nombre o número en el protocolo contienen el texto buscado. */
export const searchDetails = (
  details: ProtocolPatientStatusDetail[],
  searchText: string
): ProtocolPatientStatusDetail[] => {
  const query = normalize(searchText);
  if (!query) return details;
  return details.filter(
    (item) =>
      normalize(item.patientFullName).includes(query) || normalize(item.numberInProtocol).includes(query)
  );
};

/**
 * Orden alfabético por nombre. Los pacientes sin nombre van al final: ordenarlos
 * primero llenaría el principio de la lista de guiones.
 */
export const sortDetailsByName = (details: ProtocolPatientStatusDetail[]): ProtocolPatientStatusDetail[] =>
  details.slice().sort((a, b) => {
    const nameA = a.patientFullName?.trim() ?? '';
    const nameB = b.patientFullName?.trim() ?? '';
    if (!nameA && nameB) return 1;
    if (nameA && !nameB) return -1;
    return nameA.localeCompare(nameB, 'es', { sensitivity: 'base' });
  });

export interface StatusCatalogEntry {
  status: number;
  /** Nombre en el glosario del backend (ProtocolPatientStatusEnum). */
  name: string;
  /** Nombre que informa la API para este protocolo, si el estado tiene pacientes. */
  description?: string;
  /** Pacientes de este protocolo en el estado; 0 si el estado no se usa acá. */
  countInProtocol: number;
  color: string;
  stage: PatientStage;
}

/**
 * Nomenclador: los diez estados del glosario, siempre todos, agrupados por etapa y
 * en orden de código, con los pacientes que este protocolo tiene en cada uno.
 * Figuran también los estados sin pacientes: sirve para consultar qué significa un
 * código aunque este protocolo nunca lo haya usado.
 */
export const buildStatusCatalog = (
  currentProtocol: ProtocolPatientStatusStatistics | undefined
): StatusCatalogEntry[] => {
  const byStatus = new Map<number, { count: number; description?: string }>();
  (currentProtocol?.statuses ?? []).forEach((item) =>
    byStatus.set(item.status, { count: item.count, description: item.statusDescription?.trim() || undefined })
  );

  return Object.values(ProtocolPatientStatusEnum)
    .filter((value): value is number => typeof value === 'number')
    .map((status) => ({
      status,
      name: ProtocolPatientStatusEnum[status],
      description: byStatus.get(status)?.description,
      countInProtocol: byStatus.get(status)?.count ?? 0,
      color: statusColor(status),
      stage: statusStage(status)
    }))
    .sort(byStageThenStatus);
};

/**
 * Suma las estadísticas de varios protocolos en una sola: los mismos estados, con
 * los pacientes de todos. El nombre de cada estado es el primero no vacío que
 * informe cualquiera de ellos. Sirve para la vista de toda la cartera.
 */
export const mergeStatistics = (
  list: ProtocolPatientStatusStatistics[] | undefined
): ProtocolPatientStatusStatistics | undefined => {
  if (!list || list.length === 0) return undefined;

  const byStatus = new Map<number, { count: number; description?: string }>();
  let total = 0;

  list.forEach((protocol) => {
    total += protocol.total || 0;
    (protocol.statuses ?? []).forEach((item) => {
      const current = byStatus.get(item.status) ?? { count: 0 };
      current.count += item.count || 0;
      const description = item.statusDescription?.trim();
      if (!current.description && description) current.description = description;
      byStatus.set(item.status, current);
    });
  });

  return {
    protocolId: 0,
    total,
    statuses: Array.from(byStatus.entries()).map(([status, value]) => ({
      status,
      count: value.count,
      statusDescription: value.description ?? null
    }))
  };
};
